import { Clock } from "lucide-react";

interface Props {
  remainingMs: number;
}

function formatMMSS(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${s.toString().padStart(2, "0")}`;
}

/**
 * Idle-timeout warning. Shown when the user has been inactive for ≥55 min,
 * counting down the final 5 minutes before automatic sign-out. It is dismissed
 * silently by any qualifying activity (mouse/keyboard/scroll/touch/fetch).
 *
 *  - Desktop / tablet (≥768px): sticky strip directly below the app header
 *    (which is h-16). Non-modal, polite live region, does not trap focus.
 *  - Mobile (<768px): bottom-anchored strip, single line.
 */
export function IdleTimeoutBanner({ remainingMs }: Props) {
  const countdown = formatMMSS(remainingMs);

  return (
    <>
      {/* Desktop / tablet */}
      <div
        role="status"
        aria-live="polite"
        className="hidden md:flex fixed left-0 right-0 top-16 z-40 items-center justify-between gap-4 bg-teal-700 px-6 py-2.5 text-white shadow-md"
      >
        <div className="flex items-center gap-3 text-sm">
          <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
          <span>
            You'll be signed out in <span className="font-semibold tabular-nums">{countdown}</span> — keep
            using the app to stay signed in.
          </span>
        </div>
        <span className="text-sm font-semibold tabular-nums" aria-hidden="true">
          {countdown}
        </span>
      </div>

      {/* Mobile */}
      <div
        role="status"
        aria-live="polite"
        className="md:hidden fixed left-0 right-0 bottom-0 z-40 flex items-center justify-center gap-2 bg-teal-700 px-4 py-2 text-white text-sm shadow-lg"
      >
        <Clock className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span>
          Signing out in <span className="font-semibold tabular-nums">{countdown}</span>
        </span>
      </div>
    </>
  );
}
